import Dialog from "./Dialog";
import { Eye, FileText } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { relancesAPI } from "../services/api";
import { formatRelanceDate } from "./relanceDates";
import PenaliteBreakdown from "./PenaliteBreakdown";

const RelanceEmailPreview = ({ factureId, numero, paliers, initialPalier = null }) => {
  const [open, setOpen] = useState(false);
  const [palier, setPalier] = useState("today");
  const [email, setEmail] = useState(null);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const triggerRef = useRef(null);
  const pdfRequest = useRef(0);
  const days = paliers || email?.paliers || [3, 10, 20];

  const close = () => {
    pdfRequest.current += 1;
    setOpen(false);
    setPdfUrl(null);
    setPdfLoading(false);
    setPdfError(null);
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEmail(null);
    setError(null);
    relancesAPI.previewEmail(factureId, palier === "today" ? null : Number(palier))
      .then((response) => { if (!cancelled) setEmail(response.data); })
      .catch((failure) => {
        if (cancelled) return;
        const detail = failure?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Impossible de charger l'aperçu du mail");
      });
    return () => { cancelled = true; };
  }, [open, factureId, palier]);

  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);
  useEffect(() => () => { pdfRequest.current += 1; }, []);

  const showPdf = async () => {
    const request = ++pdfRequest.current;
    setPdfLoading(true);
    setPdfError(null);
    try {
      const response = await relancesAPI.previewAttachment(factureId);
      if (request !== pdfRequest.current) return;
      setPdfUrl(URL.createObjectURL(new Blob([response.data], { type: "application/pdf" })));
    } catch (failure) {
      if (request !== pdfRequest.current) return;
      const detail = failure?.response?.data?.detail;
      setPdfError(typeof detail === "string" ? detail : "Impossible de charger le PDF joint");
    } finally {
      if (request === pdfRequest.current) setPdfLoading(false);
    }
  };

  return <>
    <button ref={triggerRef} type="button" className="inline-flex items-center gap-1 text-primary-700 hover:underline"
      onClick={() => { setEmail(null); setError(null); setPalier(initialPalier === null ? "today" : String(initialPalier)); setOpen(true); }}>
      <Eye className="h-3.5 w-3.5" /> Aperçu du mail
    </button>
    {open && <Dialog title="Aperçu du mail de rappel" onClose={close} closeLabel="Fermer l'aperçu" returnFocusRef={triggerRef} size="max-w-3xl">
      <p className="mb-4 text-sm text-gray-500">{numero} · Aucun email envoyé depuis cet aperçu</p>
      <div className="space-y-5">
            <label className="block text-sm font-medium text-gray-700">
              Situation à prévisualiser
              <select className="input-field mt-1" value={palier} onChange={(event) => setPalier(event.target.value)}>
                <option value="today">Aujourd'hui</option>
                <option value="-3">J-3 : rappel avant échéance</option>
                {days.map((day) => <option key={day} value={day}>J+{day} : relance après échéance</option>)}
              </select>
            </label>
            {error ? <p role="alert" className="text-sm text-red-700">{error}</p> : !email ? (
              <p role="status" className="text-sm text-gray-500">Chargement de l'aperçu...</p>
            ) : <>
              <p className="text-xs text-gray-500">
                {palier === "today" ? "Situation au" : "Simulation au"} {formatRelanceDate(email.date_reference)}
                {email.date_echeance && ` · Échéance : ${formatRelanceDate(email.date_echeance)}`}
              </p>
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <dl className="bg-gray-50 p-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm">
                  <dt className="text-gray-500">De</dt><dd className="text-gray-900 break-words">{email.expediteur}</dd>
                  <dt className="text-gray-500">À</dt><dd className="text-gray-900 break-words">{email.destinataire}</dd>
                  {email.copie_cachee && <><dt className="text-gray-500">Cci</dt><dd className="text-gray-900 break-words">{email.copie_cachee}</dd></>}
                  <dt className="text-gray-500">Objet</dt><dd className="text-gray-900 font-medium break-words">{email.objet}</dd>
                </dl>
                <pre className="p-4 sm:p-6 whitespace-pre-wrap break-words font-sans text-sm leading-6 text-gray-800 border-t border-gray-200">{email.corps}</pre>
              </div>
              <PenaliteBreakdown calcul={email.penalites} />
              <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                  <span className="flex items-center gap-2 text-gray-700 break-all">
                    <FileText className="h-4 w-4 shrink-0" /> {email.piece_jointe}
                  </span>
                  {!pdfUrl && <button type="button" onClick={showPdf} disabled={pdfLoading} className="text-primary-700 hover:underline disabled:opacity-50">
                    {pdfLoading ? "Chargement du PDF..." : "Voir le PDF joint"}
                  </button>}
                </div>
                {pdfError && <p role="alert" className="text-sm text-red-700">{pdfError}</p>}
                {pdfUrl && <iframe src={pdfUrl} title="Aperçu du PDF joint" className="w-full h-96 rounded border border-gray-200" />}
              </div>
            </>}
          </div>
    </Dialog>}
  </>;
};

export default RelanceEmailPreview;
